import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Alert,
  Paper,
  Tabs,
  Tab,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Badge,
  Switch,
  FormControlLabel,
  Slider,
  Stack,
  Rating,
} from '@mui/material';
import {
  PersonAdd as LeadIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Analytics as AnalyticsIcon,
  Assessment as AssessmentIcon,
  Compare as CompareIcon,
  Timeline as TimelineIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Today as TodayIcon,
  Event as EventIcon,
  Block as BlockIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Speed as SpeedIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Chat as ChatIcon,
  Web as WebIcon,
  Campaign as CampaignIcon,
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';

function TabPanel({
  children, value, index, ...other
}) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`lead-tabpanel-${index}`}
      aria-labelledby={`lead-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function LeadGenerationSystem({ property, onUpdate }) {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedLead, setSelectedLead] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [leadSettings, setLeadSettings] = useState({
    autoScoring: true,
    autoNurturing: true,
    emailCampaigns: true,
    smsCampaigns: false,
    followUpDays: 3,
    maxFollowUps: 5,
    scoringWeights: {
      budget: 25,
      timeline: 20,
      urgency: 15,
      engagement: 20,
      source: 20,
    },
  });

  // Mock lead data
  const mockLeads = [
    {
      id: 1,
      firstName: 'John',
      lastName: 'Smith',
      email: 'john.smith@email.com',
      phone: '(555) 123-4567',
      source: 'Website',
      status: 'New',
      score: 85,
      budget: 1200000,
      timeline: '3-6 months',
      urgency: 'High',
      preferredLocation: 'Downtown',
      propertyType: 'Single Family',
      beds: 3,
      baths: 2,
      sqft: 2500,
      notes: 'Interested in properties with good schools and parking',
      createdAt: new Date('2024-07-15'),
      lastContact: new Date('2024-07-18'),
      nextFollowUp: new Date('2024-07-21'),
      agent: 'Sarah Johnson',
      engagement: {
        emailsOpened: 3,
        emailsClicked: 2,
        websiteVisits: 5,
        propertyViews: 8,
        phoneCalls: 1,
      },
      activities: [
        {
          type: 'Email Sent',
          date: new Date('2024-07-18'),
          description: 'Welcome email with property recommendations',
          status: 'Completed',
        },
        {
          type: 'Phone Call',
          date: new Date('2024-07-19'),
          description: 'Initial consultation call',
          status: 'Completed',
        },
        {
          type: 'Property View',
          date: new Date('2024-07-20'),
          description: 'Viewed 3 properties online',
          status: 'Completed',
        },
        {
          type: 'Follow-up Email',
          date: new Date('2024-07-21'),
          description: 'Scheduled for today',
          status: 'Scheduled',
        },
      ],
      tags: ['High Priority', 'First-time Buyer', 'Downtown'],
    },
    {
      id: 2,
      firstName: 'Sarah',
      lastName: 'Johnson',
      email: 'sarah.j@email.com',
      phone: '(555) 987-6543',
      source: 'Referral',
      status: 'Qualified',
      score: 92,
      budget: 1500000,
      timeline: '1-2 months',
      urgency: 'Very High',
      preferredLocation: 'Suburbs',
      propertyType: 'Townhouse',
      beds: 4,
      baths: 3,
      sqft: 3000,
      notes: 'Relocating from out of state, needs quick closing',
      createdAt: new Date('2024-07-10'),
      lastContact: new Date('2024-07-20'),
      nextFollowUp: new Date('2024-07-23'),
      agent: 'Mike Davis',
      engagement: {
        emailsOpened: 5,
        emailsClicked: 4,
        websiteVisits: 12,
        propertyViews: 15,
        phoneCalls: 3,
      },
      activities: [
        {
          type: 'Referral Received',
          date: new Date('2024-07-10'),
          description: 'Referred by existing client',
          status: 'Completed',
        },
        {
          type: 'Initial Consultation',
          date: new Date('2024-07-12'),
          description: 'Video call consultation',
          status: 'Completed',
        },
        {
          type: 'Property Tour',
          date: new Date('2024-07-15'),
          description: 'In-person tour of 2 properties',
          status: 'Completed',
        },
        {
          type: 'Offer Preparation',
          date: new Date('2024-07-20'),
          description: 'Preparing offer for preferred property',
          status: 'In Progress',
        },
      ],
      tags: ['Relocation', 'High Budget', 'Quick Close'],
    },
    {
      id: 3,
      firstName: 'Mike',
      lastName: 'Davis',
      email: 'mike.davis@email.com',
      phone: '(555) 456-7890',
      source: 'Social Media',
      status: 'Prospect',
      score: 65,
      budget: 800000,
      timeline: '6-12 months',
      urgency: 'Low',
      preferredLocation: 'Any',
      propertyType: 'Condo',
      beds: 2,
      baths: 2,
      sqft: 1200,
      notes: 'Just starting to look, gathering information',
      createdAt: new Date('2024-07-05'),
      lastContact: new Date('2024-07-12'),
      nextFollowUp: new Date('2024-07-25'),
      agent: 'Lisa Wilson',
      engagement: {
        emailsOpened: 1,
        emailsClicked: 0,
        websiteVisits: 2,
        propertyViews: 3,
        phoneCalls: 0,
      },
      activities: [
        {
          type: 'Lead Captured',
          date: new Date('2024-07-05'),
          description: 'Downloaded market report',
          status: 'Completed',
        },
        {
          type: 'Welcome Email',
          date: new Date('2024-07-06'),
          description: 'Welcome email sent',
          status: 'Completed',
        },
        {
          type: 'Follow-up Email',
          date: new Date('2024-07-12'),
          description: 'Market update email',
          status: 'Completed',
        },
      ],
      tags: ['Early Stage', 'Information Gathering', 'Low Budget'],
    },
  ];

  const [leads, setLeads] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [filterScore, setFilterScore] = useState('all');

  useEffect(() => {
    if (property) {
      setLeads(mockLeads);
    }
  }, [property]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAddLead = () => {
    setSelectedLead(null);
    setOpenDialog(true);
  };

  const handleEditLead = (lead) => {
    setSelectedLead(lead);
    setOpenDialog(true);
  };

  const handleDeleteLead = (leadId) => {
    setLeads((prev) => prev.filter((l) => l.id !== leadId));
  };

  const handleSaveLead = (leadData) => {
    if (selectedLead) {
      setLeads((prev) => prev.map((l) => (l.id === selectedLead.id ? { ...l, ...leadData } : l)));
    } else {
      const newLead = {
        id: Date.now(),
        ...leadData,
        status: 'New',
        score: calculateLeadScore(leadData),
        createdAt: new Date(),
        lastContact: new Date(),
        nextFollowUp: new Date(Date.now() + leadSettings.followUpDays * 24 * 60 * 60 * 1000),
        engagement: {
          emailsOpened: 0,
          emailsClicked: 0,
          websiteVisits: 0,
          propertyViews: 0,
          phoneCalls: 0,
        },
        activities: [],
        tags: [],
      };
      setLeads((prev) => [...prev, newLead]);
    }
    setOpenDialog(false);
  };

  const calculateLeadScore = (leadData) => {
    let score = 0;
    const weights = leadSettings.scoringWeights;

    // Budget scoring
    if (leadData.budget >= 1500000) score += weights.budget;
    else if (leadData.budget >= 1000000) score += weights.budget * 0.8;
    else if (leadData.budget >= 500000) score += weights.budget * 0.6;
    else score += weights.budget * 0.4;

    // Timeline scoring
    if (leadData.timeline === '1-2 months') score += weights.timeline;
    else if (leadData.timeline === '3-6 months') score += weights.timeline * 0.8;
    else if (leadData.timeline === '6-12 months') score += weights.timeline * 0.6;
    else score += weights.timeline * 0.4;

    // Urgency scoring
    if (leadData.urgency === 'Very High') score += weights.urgency;
    else if (leadData.urgency === 'High') score += weights.urgency * 0.8;
    else if (leadData.urgency === 'Medium') score += weights.urgency * 0.6;
    else score += weights.urgency * 0.4;

    // Source scoring
    if (leadData.source === 'Referral') score += weights.source;
    else if (leadData.source === 'Website') score += weights.source * 0.8;
    else if (leadData.source === 'Social Media') score += weights.source * 0.6;
    else score += weights.source * 0.4;

    return Math.round(score);
  };

  const getFilteredLeads = () => {
    let filtered = leads;

    if (filterStatus !== 'all') {
      filtered = filtered.filter((l) => l.status === filterStatus);
    }

    if (filterSource !== 'all') {
      filtered = filtered.filter((l) => l.source === filterSource);
    }

    if (filterScore !== 'all') {
      switch (filterScore) {
        case 'high':
          filtered = filtered.filter((l) => l.score >= 80);
          break;
        case 'medium':
          filtered = filtered.filter((l) => l.score >= 60 && l.score < 80);
          break;
        case 'low':
          filtered = filtered.filter((l) => l.score < 60);
          break;
      }
    }

    return filtered.sort((a, b) => b.score - a.score);
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

  const formatDate = (date) => new Date(date).toLocaleDateString();

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'New': return 'primary';
      case 'Qualified': return 'success';
      case 'Prospect': return 'warning';
      case 'Converted': return 'success';
      case 'Lost': return 'error';
      default: return 'default';
    }
  };

  const renderLeadList = () => (
    <Box>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2,
      }}
      >
        <Typography variant="h6">
          Lead Management (
          {getFilteredLeads().length}
          {' '}
          leads)
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddLead}
        >
          Add Lead
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="New">New</MenuItem>
                  <MenuItem value="Qualified">Qualified</MenuItem>
                  <MenuItem value="Prospect">Prospect</MenuItem>
                  <MenuItem value="Converted">Converted</MenuItem>
                  <MenuItem value="Lost">Lost</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Source</InputLabel>
                <Select
                  value={filterSource}
                  onChange={(e) => setFilterSource(e.target.value)}
                  label="Source"
                >
                  <MenuItem value="all">All Sources</MenuItem>
                  <MenuItem value="Website">Website</MenuItem>
                  <MenuItem value="Referral">Referral</MenuItem>
                  <MenuItem value="Social Media">Social Media</MenuItem>
                  <MenuItem value="Cold Call">Cold Call</MenuItem>
                  <MenuItem value="Open House">Open House</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Score</InputLabel>
                <Select
                  value={filterScore}
                  onChange={(e) => setFilterScore(e.target.value)}
                  label="Score"
                >
                  <MenuItem value="all">All Scores</MenuItem>
                  <MenuItem value="high">High (80+)</MenuItem>
                  <MenuItem value="medium">Medium (60-79)</MenuItem>
                  <MenuItem value="low">Low (&lt;60)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button variant="outlined" startIcon={<RefreshIcon />}>
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Lead Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" fontWeight={700} color="primary">
                {leads.filter((l) => l.status === 'New').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                New Leads
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" fontWeight={700} color="success.main">
                {leads.filter((l) => l.status === 'Qualified').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Qualified Leads
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" fontWeight={700} color="warning.main">
                {leads.filter((l) => l.score >= 80).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                High Score Leads
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" fontWeight={700} color="info.main">
                {leads.filter((l) => l.status === 'Converted').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Converted
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Leads List */}
      <Card>
        <CardContent>
          {getFilteredLeads().length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
              <LeadIcon sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h6">No leads found</Typography>
              <Typography variant="body2">Try adjusting your filters or add new leads</Typography>
            </Box>
          ) : (
            <List>
              {getFilteredLeads().map((lead, index) => (
                <React.Fragment key={lead.id}>
                  <ListItem>
                    <ListItemAvatar>
                      <Badge
                        badgeContent={lead.score}
                        color={getScoreColor(lead.score)}
                      >
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <LeadIcon />
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {lead.firstName}
                            {' '}
                            {lead.lastName}
                          </Typography>
                          <Chip
                            label={lead.status}
                            color={getStatusColor(lead.status)}
                            size="small"
                          />
                          <Chip
                            label={lead.source}
                            variant="outlined"
                            size="small"
                          />
                          <Chip
                            label={`Score: ${lead.score}`}
                            color={getScoreColor(lead.score)}
                            size="small"
                          />
                        </Box>
                      )}
                      secondary={(
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {lead.email}
                            {' '}
                            •
                            {lead.phone}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Budget:
                            {' '}
                            {formatCurrency(lead.budget)}
                            {' '}
                            • Timeline:
                            {' '}
                            {lead.timeline}
                            {' '}
                            • Urgency:
                            {' '}
                            {lead.urgency}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {lead.beds}
                            {' '}
                            bed,
                            {lead.baths}
                            {' '}
                            bath •
                            {lead.sqft}
                            {' '}
                            sqft •
                            {lead.preferredLocation}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Agent:
                            {' '}
                            {lead.agent}
                            {' '}
                            • Created:
                            {' '}
                            {formatDate(lead.createdAt)}
                          </Typography>
                          {lead.notes && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Notes:
                              {' '}
                              {lead.notes}
                            </Typography>
                          )}
                          {lead.tags.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              {lead.tags.map((tag, idx) => (
                                <Chip key={idx} label={tag} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                              ))}
                            </Box>
                          )}
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Next Follow-up:
                              {' '}
                              {formatDate(lead.nextFollowUp)}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton onClick={() => setSelectedLead(lead)}>
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Lead">
                        <IconButton onClick={() => handleEditLead(lead)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Lead">
                        <IconButton onClick={() => handleDeleteLead(lead.id)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItem>
                  {index < getFilteredLeads().length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  const renderLeadActivities = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Lead Activities & Timeline
      </Typography>

      {selectedLead ? (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {selectedLead.firstName}
              {' '}
              {selectedLead.lastName}
              {' '}
              - Activity Timeline
            </Typography>

            <Box sx={{ mt: 2 }}>
              {selectedLead.activities.map((activity, index) => (
                <Box key={index} sx={{ display: 'flex', mb: 3 }}>
                  {/* Timeline dot */}
                  <Box sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: activity.status === 'Completed' ? 'success.main' : 'primary.main',
                    mt: 1,
                    mr: 2,
                    flexShrink: 0,
                  }}
                  />

                  {/* Timeline line */}
                  {index < selectedLead.activities.length - 1 && (
                    <Box sx={{
                      width: 2,
                      height: 40,
                      bgcolor: 'grey.300',
                      ml: 5,
                      mt: -2,
                      position: 'absolute',
                    }}
                    />
                  )}

                  {/* Content */}
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 1, mb: 1,
                    }}
                    >
                      <Typography variant="subtitle2" fontWeight={600}>
                        {activity.type}
                      </Typography>
                      <Chip
                        label={activity.status}
                        size="small"
                        color={activity.status === 'Completed' ? 'success' : 'primary'}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {activity.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(activity.date)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Alert severity="info">
          Select a lead from the list to view their activity timeline
        </Alert>
      )}
    </Box>
  );

  const renderAutomationSettings = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Lead Automation Settings
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Lead Scoring
              </Typography>

              <FormControlLabel
                control={(
                  <Switch
                    checked={leadSettings.autoScoring}
                    onChange={(e) => setLeadSettings((prev) => ({
                      ...prev,
                      autoScoring: e.target.checked,
                    }))}
                  />
                )}
                label="Enable automatic lead scoring"
              />

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Scoring Weights
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2">Budget</Typography>
                    <Slider
                      value={leadSettings.scoringWeights.budget}
                      onChange={(e, value) => setLeadSettings((prev) => ({
                        ...prev,
                        scoringWeights: { ...prev.scoringWeights, budget: value },
                      }))}
                      min={0}
                      max={50}
                      valueLabelDisplay="auto"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">Timeline</Typography>
                    <Slider
                      value={leadSettings.scoringWeights.timeline}
                      onChange={(e, value) => setLeadSettings((prev) => ({
                        ...prev,
                        scoringWeights: { ...prev.scoringWeights, timeline: value },
                      }))}
                      min={0}
                      max={50}
                      valueLabelDisplay="auto"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">Urgency</Typography>
                    <Slider
                      value={leadSettings.scoringWeights.urgency}
                      onChange={(e, value) => setLeadSettings((prev) => ({
                        ...prev,
                        scoringWeights: { ...prev.scoringWeights, urgency: value },
                      }))}
                      min={0}
                      max={50}
                      valueLabelDisplay="auto"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">Source</Typography>
                    <Slider
                      value={leadSettings.scoringWeights.source}
                      onChange={(e, value) => setLeadSettings((prev) => ({
                        ...prev,
                        scoringWeights: { ...prev.scoringWeights, source: value },
                      }))}
                      min={0}
                      max={50}
                      valueLabelDisplay="auto"
                    />
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Lead Nurturing
              </Typography>

              <FormControlLabel
                control={(
                  <Switch
                    checked={leadSettings.autoNurturing}
                    onChange={(e) => setLeadSettings((prev) => ({
                      ...prev,
                      autoNurturing: e.target.checked,
                    }))}
                  />
                )}
                label="Enable automatic lead nurturing"
              />

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Follow-up Settings
                </Typography>
                <TextField
                  label="Days between follow-ups"
                  type="number"
                  value={leadSettings.followUpDays}
                  onChange={(e) => setLeadSettings((prev) => ({
                    ...prev,
                    followUpDays: parseInt(e.target.value) || 3,
                  }))}
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Maximum follow-ups"
                  type="number"
                  value={leadSettings.maxFollowUps}
                  onChange={(e) => setLeadSettings((prev) => ({
                    ...prev,
                    maxFollowUps: parseInt(e.target.value) || 5,
                  }))}
                  fullWidth
                  size="small"
                />
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Communication Channels
                </Typography>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={leadSettings.emailCampaigns}
                      onChange={(e) => setLeadSettings((prev) => ({
                        ...prev,
                        emailCampaigns: e.target.checked,
                      }))}
                    />
                  )}
                  label="Email campaigns"
                />
                <FormControlLabel
                  control={(
                    <Switch
                      checked={leadSettings.smsCampaigns}
                      onChange={(e) => setLeadSettings((prev) => ({
                        ...prev,
                        smsCampaigns: e.target.checked,
                      }))}
                    />
                  )}
                  label="SMS campaigns"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Lead Generation & Management
      </Typography>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Lead Management" icon={<LeadIcon />} />
        <Tab label="Activities" icon={<TimelineIcon />} />
        <Tab label="Automation" icon={<SettingsIcon />} />
      </Tabs>

      <TabPanel value={activeTab} index={0}>
        {renderLeadList()}
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        {renderLeadActivities()}
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        {renderAutomationSettings()}
      </TabPanel>

      {/* Add/Edit Lead Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedLead ? 'Edit Lead' : 'Add New Lead'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name *"
                value={selectedLead?.firstName || ''}
                onChange={(e) => setSelectedLead((prev) => ({
                  ...prev,
                  firstName: e.target.value,
                }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name *"
                value={selectedLead?.lastName || ''}
                onChange={(e) => setSelectedLead((prev) => ({
                  ...prev,
                  lastName: e.target.value,
                }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email *"
                value={selectedLead?.email || ''}
                onChange={(e) => setSelectedLead((prev) => ({
                  ...prev,
                  email: e.target.value,
                }))}
                fullWidth
                type="email"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone"
                value={selectedLead?.phone || ''}
                onChange={(e) => setSelectedLead((prev) => ({
                  ...prev,
                  phone: e.target.value,
                }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Source</InputLabel>
                <Select
                  value={selectedLead?.source || ''}
                  onChange={(e) => setSelectedLead((prev) => ({
                    ...prev,
                    source: e.target.value,
                  }))}
                  label="Source"
                >
                  <MenuItem value="Website">Website</MenuItem>
                  <MenuItem value="Referral">Referral</MenuItem>
                  <MenuItem value="Social Media">Social Media</MenuItem>
                  <MenuItem value="Cold Call">Cold Call</MenuItem>
                  <MenuItem value="Open House">Open House</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Budget *"
                value={selectedLead?.budget || ''}
                onChange={(e) => setSelectedLead((prev) => ({
                  ...prev,
                  budget: parseFloat(e.target.value) || 0,
                }))}
                fullWidth
                type="number"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Timeline</InputLabel>
                <Select
                  value={selectedLead?.timeline || ''}
                  onChange={(e) => setSelectedLead((prev) => ({
                    ...prev,
                    timeline: e.target.value,
                  }))}
                  label="Timeline"
                >
                  <MenuItem value="1-2 months">1-2 months</MenuItem>
                  <MenuItem value="3-6 months">3-6 months</MenuItem>
                  <MenuItem value="6-12 months">6-12 months</MenuItem>
                  <MenuItem value="12+ months">12+ months</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Urgency</InputLabel>
                <Select
                  value={selectedLead?.urgency || ''}
                  onChange={(e) => setSelectedLead((prev) => ({
                    ...prev,
                    urgency: e.target.value,
                  }))}
                  label="Urgency"
                >
                  <MenuItem value="Very High">Very High</MenuItem>
                  <MenuItem value="High">High</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="Low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Preferred Location"
                value={selectedLead?.preferredLocation || ''}
                onChange={(e) => setSelectedLead((prev) => ({
                  ...prev,
                  preferredLocation: e.target.value,
                }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Property Type</InputLabel>
                <Select
                  value={selectedLead?.propertyType || ''}
                  onChange={(e) => setSelectedLead((prev) => ({
                    ...prev,
                    propertyType: e.target.value,
                  }))}
                  label="Property Type"
                >
                  <MenuItem value="Single Family">Single Family</MenuItem>
                  <MenuItem value="Townhouse">Townhouse</MenuItem>
                  <MenuItem value="Condo">Condo</MenuItem>
                  <MenuItem value="Multi-family">Multi-family</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Bedrooms"
                value={selectedLead?.beds || ''}
                onChange={(e) => setSelectedLead((prev) => ({
                  ...prev,
                  beds: parseInt(e.target.value) || 0,
                }))}
                fullWidth
                type="number"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Bathrooms"
                value={selectedLead?.baths || ''}
                onChange={(e) => setSelectedLead((prev) => ({
                  ...prev,
                  baths: parseFloat(e.target.value) || 0,
                }))}
                fullWidth
                type="number"
                step="0.5"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Square Feet"
                value={selectedLead?.sqft || ''}
                onChange={(e) => setSelectedLead((prev) => ({
                  ...prev,
                  sqft: parseFloat(e.target.value) || 0,
                }))}
                fullWidth
                type="number"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                value={selectedLead?.notes || ''}
                onChange={(e) => setSelectedLead((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))}
                fullWidth
                multiline
                rows={3}
                placeholder="Enter any notes about this lead..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={() => handleSaveLead(selectedLead)}
            variant="contained"
            disabled={!selectedLead?.firstName || !selectedLead?.lastName || !selectedLead?.email || !selectedLead?.budget}
          >
            {selectedLead?.id ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LeadGenerationSystem;
